import { Injectable, inject } from '@angular/core';
import {
  injectMutation,
  injectQuery,
  injectQueryClient,
} from '@tanstack/angular-query-experimental';
import { firstValueFrom } from 'rxjs';

import { Client, RequestVerificationBody } from '../../api/api-clients';
import type { AppVerificationRequest } from '../models/verification-request.model';
import { toAppVerificationRequest } from '../models/verification-request.model';
import { verificationKeys } from './query-keys';

@Injectable({ providedIn: 'root' })
export class VerificationService {
  private readonly client = inject(Client);
  private readonly queryClient = injectQueryClient();

  /**
   * Fetches the current user's verification request.
   * Returns `null` if no verification request has been submitted (204 No Content).
   */
  readonly myVerificationRequest = injectQuery(() => ({
    queryKey: verificationKeys.mine(),
    queryFn: async (): Promise<AppVerificationRequest | null> => {
      try {
        const result = await firstValueFrom(this.client.verificationRequestGET());
        return result ? toAppVerificationRequest(result) : null;
      } catch (err: any) {
        // 204 No Content — no verification request submitted yet
        if (err?.status === 204) return null;
        throw err;
      }
    },
  }));

  /**
   * Submits a new verification request for the current user's profile.
   * Invalidates the verification request query on success.
   */
  readonly submitVerificationRequest = injectMutation(() => ({
    mutationFn: (additionalInfo?: string) =>
      firstValueFrom(
        this.client.verificationRequestPOST(new RequestVerificationBody({ additionalInfo })),
      ),
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: verificationKeys.mine() });
    },
  }));
}
